import { useState } from "react";
import { Grid3X3 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface BatchTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (start: number, end: number, capacity: number) => Promise<any>;
}

export function BatchTableDialog({
  open,
  onOpenChange,
  onSubmit,
}: BatchTableDialogProps) {
  const [startNumber, setStartNumber] = useState("1");
  const [endNumber, setEndNumber] = useState("20");
  const [capacity, setCapacity] = useState("4");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const start = parseInt(startNumber) || 0;
  const end = parseInt(endNumber) || 0;
  const cap = parseInt(capacity) || 4;
  const count = end >= start ? end - start + 1 : 0;

  const handleSubmit = async () => {
    if (count <= 0 || count > 100) return;

    setIsSubmitting(true);
    try {
      await onSubmit(start, end, cap);
      setStartNumber("1");
      setEndNumber("20");
      setCapacity("4");
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Grid3X3 className="h-5 w-5" />
            Criar Mesas em Lote
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startNumber">De (número)</Label>
              <Input
                id="startNumber"
                type="number"
                min="1"
                value={startNumber}
                onChange={(e) => setStartNumber(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endNumber">Até (número)</Label>
              <Input
                id="endNumber"
                type="number"
                min="1"
                value={endNumber}
                onChange={(e) => setEndNumber(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="capacity">Capacidade (lugares)</Label>
            <Input
              id="capacity"
              type="number"
              min="1"
              max="20"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </div>

          {count > 0 && count <= 100 && (
            <div className="rounded-lg bg-muted p-3 text-center">
              <p className="text-sm">
                Serão criadas <span className="font-bold">{count}</span> mesas
              </p>
              <p className="text-xs text-muted-foreground">
                Numeradas de {String(start).padStart(2, "0")} até {String(end).padStart(2, "0")} com {cap} lugares cada
              </p>
            </div>
          )}

          {count > 100 && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Máximo de 100 mesas por vez.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || count <= 0 || count > 100}
          >
            {isSubmitting ? "Criando..." : `Criar ${count} Mesas`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
